import * as Sentry from "@sentry/react";
import {
  Navigate,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import { App } from "@src/App";
import { legalRedirects, MainRoutes, SignUpRoutes } from "@router/route-params";
import { CustomErrorBoundary } from "@router/custom-error-boundary";
import { LoginWrapper } from "@pages/login/login-wrapper";
import { VerifyEmail } from "@pages/verify-email";
import { ResendVerificationEmail } from "@pages/resend-verification-email";
import { PasswordRecovery } from "@pages/password-recovery/password-recovery";
import { PasswordChange } from "@pages/password-change/password-change";
import { Logout } from "@pages/logout";
import { CompleteAccount } from "@pages/complete-account/complete-account";
import { SSOLogin } from "@pages/sso-login/sso-login";
import { ProviderLogin } from "@pages/provider-login/provider-login";
import { ConnectLoginRedirect } from "@pages/connect-login-redirect";
import { UserModuleConverter } from "@pages/user-module-converter";
import { ExternalRedirect } from "@router/external-redirect";
import { LegalHashRedirect } from "@router/legal-hash-redirect";
import { Trial } from "@pages/trial/trial";
import { Invited } from "@pages/invited/invited";

/**
 * Sentry-instrumented `createBrowserRouter`.
 *
 * `reactRouterV7BrowserTracingIntegration` on its own only registers the client for
 * navigation instrumentation - the code that actually starts navigation spans and
 * re-names the pageload span from the raw URL to the matched route pattern lives in
 * this wrapper. Without it, route changes are never reported.
 *
 * The wrapper must be created after `Sentry.init()` has run, otherwise it silently
 * returns `createBrowserRouter` unwrapped. `main.tsx` guarantees this by importing
 * `@src/sentry-init` before this module.
 * https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/v7/#usage-with-createbrowserrouter-or-creatememoryrouter-data-mode
 */
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <Route element={<App />} errorElement={<CustomErrorBoundary />}>
      <Route path={MainRoutes.login} element={<LoginWrapper />} />

      {/* Custom Single Sign-On (SSO) login */}
      <Route path={MainRoutes.ssoLogin} element={<SSOLogin />} />

      {/* Authentication provider login */}
      <Route path={MainRoutes.providerLogin} element={<ProviderLogin />} />

      <Route path={MainRoutes.logout} element={<Logout />} />

      {/* Sign up route: /signup */}
      <Route path={MainRoutes.signup}>
        {/* Redirect /signup to /signup/trial by default */}
        <Route
          index
          element={
            <Navigate
              to={{
                pathname: SignUpRoutes.trial,
                search: window.location.search,
              }}
              replace
            />
          }
        />

        {/* Start a trial subscription route: /signup/trial */}
        <Route path={SignUpRoutes.trial} element={<Trial />} />

        {/* Sign up page for invited members */}
        <Route path={SignUpRoutes.invited} element={<Invited />} />
      </Route>

      {/* Email verification route: /verifymail */}
      <Route path={MainRoutes.verifyEmail} element={<VerifyEmail />} />

      {/* Resend verification email route: /resendEmail */}
      <Route
        path={MainRoutes.resendEmail}
        element={<ResendVerificationEmail />}
      />

      {/* Password recovery route: /passwordRecovery */}
      <Route
        path={MainRoutes.passwordRecovery}
        element={<PasswordRecovery />}
      />

      {/* Reset password route: /resetPassword */}
      <Route path={MainRoutes.resetPassword} element={<PasswordChange />} />

      {/* Complete account information: /completeAccount */}
      <Route path={MainRoutes.completeAccount} element={<CompleteAccount />} />

      {/* Login redirect route for the Connect App : /connect-login-redirect */}
      <Route
        path={MainRoutes.connectLoginRedirect}
        element={<ConnectLoginRedirect />}
      />

      {/* User Module: /user-module */}
      <Route path={MainRoutes.userModule} element={<UserModuleConverter />} />

      {/* User Module: /dashboard-login */}
      <Route
        path={MainRoutes.oldDashboardLogin}
        element={<UserModuleConverter />}
      />

      {/* WebEditor links to the Sphere Viewer: /web-editor-link/app */}
      <Route
        path={`${MainRoutes.webEditorLink}/*`}
        lazy={async () => ({
          Component: (await import("@pages/web-editor-link-redirect"))
            .WebEditorLinkRedirect,
        })}
      />

      {/* Sphere Viewer links back to the WebEditor: /viewer-link/project/:projectId */}
      <Route
        path={`${MainRoutes.viewerLink}/*`}
        lazy={async () => ({
          Component: (await import("@pages/viewer-link-redirect"))
            .ViewerLinkRedirect,
        })}
      />

      {/* Legal redirects: /legal */}
      <Route path={MainRoutes.legal}>
        {/* Dynamically create routes for all legal redirects (excluding hash routes,
        because the React browser router can't handle hash routes).
        For example: /legal/privacy-policy */}
        {Object.keys(legalRedirects).map((redirect) => (
          <Route
            key={redirect}
            path={legalRedirects[redirect].path}
            element={
              <ExternalRedirect
                targetUrl={legalRedirects[redirect].targetUrl}
              />
            }
          />
        ))}

        {/* Default legal route - handles hash fragments (because the React
        browser-router can't handle hash routes) and fallback
        For example: /legal or /legal/#privacy-policy */}
        <Route index element={<LegalHashRedirect />} />
      </Route>

      {/* Root: / */}
      <Route
        path={MainRoutes.root}
        element={
          <Navigate
            to={{
              pathname: MainRoutes.login,
              search: window.location.search,
            }}
          />
        }
      />
    </Route>
  )
);
